import { useRef, type KeyboardEvent } from 'react'
import { RangeButton, RangeTrack } from './ChartTabs.styles'

interface ChartTabOption<T extends string> {
  value: T
  label: string
}

interface ChartTabsProps<T extends string> {
  options: ChartTabOption<T>[]
  value: T
  onChange: (value: T) => void
  label: string
  /** Figma's wider tabs (the Cumulative / Monthly / NAV switch); ranges are compact. */
  wide?: boolean
  /** Test-automation hook: each option gets `data-cy="<dataCy>-<value>"`. */
  dataCy?: string
}

const STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }

/**
 * The chart's segmented control: range picker (1D … All) and view switch alike. A radio
 * group: one tab stop, and the arrow keys move the selection.
 */
export const ChartTabs = <T extends string>({
  options,
  value,
  onChange,
  label,
  wide = false,
  dataCy,
}: ChartTabsProps<T>) => {
  const trackRef = useRef<HTMLDivElement>(null)

  const onKeyDown = (event: KeyboardEvent) => {
    const step = STEP[event.key]
    if (!step) return
    event.preventDefault()
    const current = options.findIndex((option) => option.value === value)
    const next = (current + step + options.length) % options.length
    onChange(options[next].value)
    trackRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus()
  }

  return (
    <RangeTrack ref={trackRef} role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((option) => (
        <RangeButton
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          tabIndex={option.value === value ? 0 : -1}
          $active={option.value === value}
          $wide={wide}
          data-cy={dataCy && `${dataCy}-${option.value.toLowerCase()}`}
          onClick={() => onChange(option.value)}>
          {option.label}
        </RangeButton>
      ))}
    </RangeTrack>
  )
}
